import React, { useEffect, useState, useRef } from "react";
import { Checkbox } from "@progress/kendo-react-inputs";
import { filterBy, CompositeFilterDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { uploadIcon, trashIcon } from "@progress/kendo-svg-icons";
import { useFastaStore } from "@presentation/stores/FastaStore";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useInjection } from "@presentation/components/dependencyInjection";
import { ListFastaService, DeleteFastaService } from "@application/useCases";
import { UploadFastaDialog } from "@presentation/components/fasta/UploadFastaDialog";
import { FastaFile, PagedFastaFileList } from "@domain/schemas/BI";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { markItemsAsLastUsed, sortItemsByLastUsed } from "@presentation/common/utils/lastUsedOrdering";
import { INCOMPLETE_ACCENT, NEUTRAL_BORDER } from "@presentation/components/job/jobRequirements";
import { useShallow } from "zustand/react/shallow";

const FASTA_LAST_USED_STORAGE_KEY = "job-setup-fasta-last-used";
const EMPTY_FASTA_LIST: FastaFile[] = [];

const getFastaStorageKey = (fasta: FastaFile) => `${fasta.location || runtimeConfig.FASTA_CONTAINER_NAME}/${fasta.fileName}`;

interface FastaMultiSelectFormProps {
  /**
   * Set by the owner when the field is required and was left empty at submit
   * time. The picker itself has no notion of which pipeline needs a FASTA.
   */
  highlightAsIncomplete?: boolean;
}

const FastaMultiSelectForm: React.FC<FastaMultiSelectFormProps> = ({ highlightAsIncomplete = false }) => {
  const { selectedFasta, fastaList, setSelectedFasta, setFastaList } = useFastaStore(
    useShallow((state) => ({
      // Fallbacks must be referentially stable: a fresh [] here makes useShallow
      // return a new snapshot on every read, which loops useSyncExternalStore (React #185).
      selectedFasta: state.selectedFasta ?? EMPTY_FASTA_LIST,
      fastaList: state.fastaList ?? EMPTY_FASTA_LIST,
      setSelectedFasta: state.setSelectedFasta,
      setFastaList: state.setFastaList,
    })),
  );

  const { setFastaFiles } = useJobSetupStore(
    useShallow((state) => ({
      setFastaFiles: state.setJobFastaFiles,
    })),
  );

  const [showFastaDialog, setShowFastaDialog] = useState(false);
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSize, setDropdownSize] = useState({ width: 600, height: 400 });
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const dropdownReference = useRef<HTMLDivElement>(null);
  const multiSelectReference = useRef<HTMLDivElement>(null);
  const searchInputReference = useRef<HTMLInputElement>(null);

  const openFastaDialog = () => setShowFastaDialog(true);
  const closeFastaDialog = () => setShowFastaDialog(false);

  // Handle mouse events for dragging and resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setDropdownPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }

      if (!isResizing) return;

      const rect = dropdownReference.current?.getBoundingClientRect();
      if (rect) {
        setDropdownSize({
          width: Math.max(400, e.clientX - rect.left),
          height: Math.max(300, e.clientY - rect.top),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart]);

  // Position dropdown below the multiselect
  useEffect(() => {
    if (!(dropdownOpen && multiSelectReference.current)) {
      return;
    }

    const rect = multiSelectReference.current.getBoundingClientRect();

    // Calculate initial position
    let x = rect.left;
    let y = rect.bottom + 5;

    // Constrain to window boundaries
    x = Math.max(10, Math.min(x, window.innerWidth - dropdownSize.width - 10));
    y = Math.max(10, Math.min(y, window.innerHeight - dropdownSize.height - 10));

    setDropdownPosition({ x, y });

    // ✅ Focus the search bar when opening
    setTimeout(() => {
      searchInputReference.current?.focus();
    }, 0);
  }, [dropdownOpen]);

  const listFastaService = useInjection(ListFastaService);
  const deleteFastaService = useInjection(DeleteFastaService);

  useEffect(() => {
    if (fastaList.length > 0) return;

    async function fetchFastaList() {
      try {
        const list: PagedFastaFileList = await listFastaService.listFastaAsync();
        if (list.list) {
          setFastaList(sortItemsByLastUsed(list.list, FASTA_LAST_USED_STORAGE_KEY, getFastaStorageKey, (item) => item.fileName));
        }
      } catch (error) {
        console.error("Error fetching fasta list:", error);
      }
    }
    fetchFastaList();
  }, [fastaList.length, listFastaService, setFastaList]);

  const handleDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    const rect = dropdownReference.current?.getBoundingClientRect();
    if (rect) {
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
  };

  const filteredData = filter ? filterBy(fastaList, filter) : fastaList;

  const updateSelections = (selected: FastaFile[]) => {
    setSelectedFasta(selected);
    const selectedPaths = selected.map((f) => (f.location ? f.location + "/" : "") + f.fileName);
    setFastaFiles(selectedPaths);
    markItemsAsLastUsed(
      FASTA_LAST_USED_STORAGE_KEY,
      selected.map((item) => getFastaStorageKey(item)),
    );
    setFastaList(sortItemsByLastUsed(fastaList, FASTA_LAST_USED_STORAGE_KEY, getFastaStorageKey, (item) => item.fileName));
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }} ref={multiSelectReference}>
        <div style={{ width: "70%", position: "relative", cursor: "pointer" }} onClick={() => setDropdownOpen(!dropdownOpen)}>
          <div
            style={{
              border: highlightAsIncomplete ? `1px solid ${INCOMPLETE_ACCENT.border}` : `1px solid ${NEUTRAL_BORDER}`,
              borderRadius: "3px",
              padding: "8px",
              minHeight: "32px",
              backgroundColor: "white",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "4px",
            }}
          >
            {selectedFasta.length > 0 ? (
              selectedFasta.map((fasta) => (
                <span
                  key={`${fasta.location}-${fasta.fileName}`}
                  style={{
                    backgroundColor: "#e3f2fd",
                    border: "1px solid #2196f3",
                    borderRadius: "3px",
                    padding: "2px 4px 2px 8px",
                    fontSize: "12px",
                    color: "#1976d2",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {fasta.fileName}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const updated = selectedFasta.filter((f) => !(f.fileName === fasta.fileName && f.location === fasta.location));
                      updateSelections(updated);
                    }}
                    style={{
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      padding: "0 1px",
                      lineHeight: 1,
                      fontSize: "13px",
                      color: "#1976d2",
                      opacity: 0.7,
                    }}
                    title="Remove"
                  >
                    ×
                  </button>
                </span>
              ))
            ) : (
              <span style={{ fontStyle: "italic", fontSize: "13px" }}>No fasta files selected — click here to select.</span>
            )}
          </div>
        </div>
        <Button svgIcon={uploadIcon} onClick={openFastaDialog} style={{ width: "27%", borderRadius: "3px" }}>
          Upload new Fasta
        </Button>
      </div>

      {dropdownOpen && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "transparent",
              zIndex: 999,
            }}
            onClick={() => setDropdownOpen(false)}
          />
          <div
            ref={dropdownReference}
            style={{
              position: "fixed",
              left: dropdownPosition.x,
              top: dropdownPosition.y,
              width: dropdownSize.width,
              height: dropdownSize.height,
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "3px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "8px 12px",
                backgroundColor: "#f5f5f5",
                borderBottom: "1px solid #e0e0e0",
                cursor: "move",
                userSelect: "none",
                borderRadius: "4px 4px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              onMouseDown={handleDragStart}
            >
              <span style={{ fontWeight: "500", fontSize: "14px" }}>Select Fasta Files</span>
              <button
                onClick={() => setDropdownOpen(false)}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                ×
              </button>
            </div>

            {/* Filter Input */}
            <div style={{ padding: "12px" }}>
              <input
                ref={searchInputReference}
                type="text"
                placeholder="Search by filename..."
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "3px",
                  fontSize: "14px",
                }}
                onChange={(e) => {
                  const filterValue = e.target.value.trim();
                  if (filterValue) {
                    const words = filterValue.split(" ");
                    const wordFilters = words.map((word: string) => ({
                      logic: "or",
                      filters: [
                        { field: "fileName", operator: "contains", value: word, ignoreCase: true },
                        { field: "createdBy", operator: "contains", value: word, ignoreCase: true },
                      ],
                    }));
                    setFilter({ logic: "and", filters: wordFilters } as CompositeFilterDescriptor);
                  } else {
                    setFilter(undefined);
                  }
                }}
              />
            </div>

            {/* Items List */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              {filteredData.map((item) => {
                const itemLocation = item.location || runtimeConfig.FASTA_CONTAINER_NAME;
                const isSelected = selectedFasta.some((selected) => (selected.location || runtimeConfig.FASTA_CONTAINER_NAME) === itemLocation && selected.fileName === item.fileName);
                return (
                  <div
                    key={`${itemLocation}-${item.fileName}`}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 12px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const itemWithLocation = { ...item, location: itemLocation } as FastaFile;
                      const currentSelected = [...selectedFasta];
                      const index = currentSelected.findIndex(
                        (selected) => (selected.location || runtimeConfig.FASTA_CONTAINER_NAME) === itemLocation && selected.fileName === itemWithLocation.fileName,
                      );

                      if (index === -1) {
                        currentSelected.push(itemWithLocation);
                      } else {
                        currentSelected.splice(index, 1);
                      }

                      updateSelections(currentSelected);
                    }}
                  >
                    <Checkbox checked={isSelected} style={{ marginRight: 8 }} readOnly={true} />
                    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                      <span style={{ fontWeight: "500", fontSize: "14px" }}>{item.fileName}</span>
                      <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: "#666", marginTop: "2px" }}>
                        <span>{(item.size / (1024 * 1024)).toFixed(2)} MB</span>
                        <span>By: {item.createdBy}</span>
                      </div>
                    </div>
                    <Button
                      svgIcon={trashIcon}
                      fillMode="flat"
                      onClick={async (e) => {
                        e.stopPropagation();

                        if (confirm(`Are you sure you want to delete ${item.fileName}?`)) {
                          try {
                            await deleteFastaService.executeAsync(item.fileName, undefined);

                            // Convert existing selectedFasta to exclude the deleted one
                            const updatedSelected = selectedFasta.filter((f) => !(f.fileName === item.fileName && f.location === item.location));
                            updateSelections(updatedSelected);

                            // Update list
                            const updatedList = fastaList.filter((f) => !(f.fileName === item.fileName && f.location === item.location));
                            setFastaList(updatedList);
                          } catch (error) {
                            console.error("Failed to delete fasta file", error);
                            alert("Failed to delete file.");
                          }
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Resize Handle */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: "20px",
                height: "20px",
                cursor: "se-resize",
                backgroundColor: "#f0f0f0",
                borderLeft: "1px solid #ccc",
                borderTop: "1px solid #ccc",
                borderRadius: "0 0 4px 0",
              }}
              onMouseDown={handleResizeStart}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: "2px",
                  right: "2px",
                  width: "0",
                  height: "0",
                  borderLeft: "8px solid transparent",
                  borderBottom: "8px solid #999",
                }}
              />
            </div>
          </div>
        </>
      )}

      {showFastaDialog && (
        <UploadFastaDialog
          onUploadSuccess={(uploadedFasta: FastaFile) => {
            markItemsAsLastUsed(FASTA_LAST_USED_STORAGE_KEY, [getFastaStorageKey(uploadedFasta)]);
            setFastaList(sortItemsByLastUsed([uploadedFasta, ...fastaList], FASTA_LAST_USED_STORAGE_KEY, getFastaStorageKey, (item) => item.fileName));
            const newSelection = [...selectedFasta, uploadedFasta];
            updateSelections(newSelection);
            setShowFastaDialog(false);
          }}
          onCancel={closeFastaDialog}
        />
      )}
    </div>
  );
};

export default FastaMultiSelectForm;
